import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoIcon } from "@/components/ui/logo";
import { cn } from "@/lib/utils";

// Icons
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function HelpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  );
}

export type UserRole = "applicant" | "commission" | "admin";

// Feature flag for new application
const isNewApplicationEnabled = import.meta.env.VITE_FEATURE_NEW_APPLICATION === "true";

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  roles: UserRole[];
  disabled?: boolean;
  badge?: string;
}

const navItems: NavItem[] = [
  { id: "home", label: "Головна", icon: HomeIcon, href: "/dashboard", roles: ["applicant", "commission", "admin"] },
  { id: "new", label: "Нова заявка", icon: PlusIcon, href: "/application/new", roles: ["applicant"], disabled: !isNewApplicationEnabled, badge: !isNewApplicationEnabled ? "Скоро" : undefined },
  { id: "applications", label: "Мої заявки", icon: FileIcon, href: "/applications", roles: ["applicant"] },
];

const bottomItems: NavItem[] = [
  { id: "help", label: "Довідка", icon: HelpIcon, href: "/help", roles: ["applicant", "commission", "admin"] },
];

interface SidebarProps {
  userRole?: UserRole;
  collapsed: boolean;
}

export function Sidebar({ userRole = "applicant", collapsed }: SidebarProps) {
  const location = useLocation();
  const { isDevMode } = useAuth();

  const mainNav = navItems.filter((item) => item.roles.includes(userRole));
  const bottomNav = bottomItems.filter((item) => item.roles.includes(userRole));

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-white border-r-[3px] border-border/50 transition-all duration-200",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Logo */}
      <div className={cn(
        "h-16 flex items-center border-b-[3px] border-border/50",
        collapsed ? "justify-center px-0" : "px-3"
      )}>
        <Link to="/dashboard" className="flex items-center gap-2.5">
          <LogoIcon size="sm" className="shrink-0" />
          {!collapsed && (
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-[15px] text-foreground">Світлодім</span>
              {isDevMode && (
                <span
                  className="px-1.5 py-0.5 text-[9px] font-medium bg-orange-100 text-orange-600 rounded"
                  title="Режим розробника (локальна розробка)"
                >
                  DEV
                </span>
              )}
            </div>
          )}
        </Link>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 p-3 space-y-1">
        {mainNav.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + "/");

          // Disabled items (coming soon)
          if (item.disabled) {
            return (
              <div
                key={item.id}
                title={collapsed ? `${item.label} (${item.badge})` : undefined}
                className={cn(
                  "flex items-center rounded-xl cursor-not-allowed opacity-60",
                  collapsed 
                    ? "w-10 h-10 justify-center" 
                    : "gap-3 px-3 h-10 text-[13px] font-medium",
                  "text-foreground/50"
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium bg-muted text-muted-foreground rounded">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.id}
              to={item.href}
              title={collapsed ? item.label : undefined}
              className={cn(
                "flex items-center rounded-xl",
                collapsed 
                  ? "w-10 h-10 justify-center" 
                  : "gap-3 px-3 h-10 text-[13px] font-medium",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted"
              )}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Nav */}
      <div className="p-3 border-t-[3px] border-border/50 space-y-1">
        {bottomNav.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <Link
              key={item.id}
              to={item.href}
              title={collapsed ? item.label : undefined}
              className={cn(
                "flex items-center rounded-xl",
                collapsed 
                  ? "w-10 h-10 justify-center" 
                  : "gap-3 px-3 h-10 text-[13px] font-medium",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground/70 hover:text-foreground hover:bg-muted"
              )}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </div>

    </aside>
  );
}
